import {
  DefaultButton,
  Panel,
  PanelType,
  PrimaryButton,
  Spinner,
  SpinnerSize,
} from 'office-ui-fabric-react';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import {
  hasChangesSelector,
  initializeNotificationPanelState,
  isCreatingTestNotificationSelector,
  isUpdatingContentSelector,
  notificationContentDataWithDiffSelector,
} from '../../../../features/componentFeatures/notificationPanelSlice';
import { getAttr } from '../../../../features/helpers/apiHelpers';
import { notificationTypeTextResolver } from '../../../../utilities/notificationTypeTextResolver';
import { NotificationContent } from './NotificationContent';

const NotificationPanel = ({
  content,
  notificationType,
  isOpen,
  onClose,
  onClickSaveButton,
  onDownloadNotification,
  onSendNotification,
  headerText,
  isFetching,
}) => {
  const { t: translator } = useTranslation();
  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(initializeNotificationPanelState());
    return function cleanup() {
      dispatch(initializeNotificationPanelState());
    };
  }, [dispatch]);

  const notificationTypeValue = getAttr(notificationType, 'notificationType');

  const notificationTypeText = notificationTypeTextResolver(
    translator,
    notificationTypeValue,
  );
  const contentToRender = useSelector(
    notificationContentDataWithDiffSelector(content),
  );
  const hasChanges = useSelector(hasChangesSelector);
  const isUpdatingContent = useSelector(isUpdatingContentSelector);
  const isCreatingTestNotification = useSelector(
    isCreatingTestNotificationSelector,
  );

  const onCloseLocal = () => onClose(hasChanges);

  const onClickDownloadNotification = () =>
    onDownloadNotification(notificationTypeValue);

  const onClickSendEmailNotification = () =>
    onSendNotification(notificationTypeValue);

  return (
    <div>
      <Panel
        isOpen={isOpen}
        type={PanelType.medium}
        onDismiss={isUpdatingContent || onCloseLocal}
        isLightDismiss={true}
        onOuterClick={(_) => null}
        closeButtonAriaLabel={translator('ariaLabel.panelClose')}
      >
        <div className="notification-panel">
          <div className="panel-content">
            <h3 className="panel-header">{headerText}</h3>
            <div className="panel-subheader">
              <strong>
                {translator('components.notificationPanel.notificationType')}:{' '}
              </strong>
              <span>{notificationTypeText || '-'}</span>
            </div>
            <div className="panel-download-buttons">
              <PrimaryButton
                text={translator(
                  'components.notificationPanel.downloadNotificationButton',
                )}
                onClick={onClickDownloadNotification}
              />
              <PrimaryButton
                text={translator(
                  'components.notificationPanel.sendNotificationEmailButton',
                )}
                onClick={onClickSendEmailNotification}
              />
              {isCreatingTestNotification && (
                <Spinner size={SpinnerSize.large} />
              )}
            </div>
            {isFetching ? (
              <Spinner size={SpinnerSize.large} />
            ) : (
              contentToRender.map((nc, i) => (
                <NotificationContent key={i} notificationContentData={nc} />
              ))
            )}
          </div>
          <div className="panel-buttons">
            {isUpdatingContent && <Spinner size={SpinnerSize.large} />}
            <DefaultButton
              text={translator('global.cancel')}
              onClick={onCloseLocal}
              disabled={isUpdatingContent}
            />
            <PrimaryButton
              text={translator('global.save')}
              onClick={onClickSaveButton}
              disabled={isUpdatingContent || !hasChanges}
            />
          </div>
        </div>
      </Panel>
    </div>
  );
};

export { NotificationPanel };
